import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { Subscription } from 'rxjs';

import { LoadingStatus } from '../../../../core/domain/events/state_provider';
import { Dietitian } from '../../../../data/dietitian/domain/dietitian';
import {
  Notification,
  NotificationType,
} from '../../../../data/dietitian/domain/notification/notification';
import { ConversationRead } from '../../../../data/message/domain/events/conversation/conversation_events';
import { IncomingMessageEventProvider } from '../../../../data/notification/incoming-message-event-provider';
import { Patient } from '../../../../data/patient/domain/patient';
import { SegmentedNavigationItemModel } from '../../../dashboard/components/segmented-navigation/components/segmented-navigation-item/segmented-navigation-item.model';
import { SegmentedNavigationComponent } from '../../../dashboard/components/segmented-navigation/segmented-navigation.component';
import { MyDietitianStateProvider } from '../../my-dietitian-state.provider';
import { MyTopbarFacade } from './my-topbar.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-my-topbar',
  templateUrl: './my-topbar.component.html',
  styleUrls: ['./my-topbar.component.scss'],
  providers: [MyTopbarFacade],
})
export class MyTopbarComponent implements OnInit, OnDestroy {
  @ViewChild('tabs')
  private tabs: SegmentedNavigationComponent;
  @Input() patient: Patient;

  public notifications?: Notification[];
  public isLoadingNotifications = false;
  public notificationsError?: string;
  public markingAsReadNotification?: Notification;
  public loadingMarkAsReadAll: false;

  public segmentedItems: SegmentedNavigationItemModel[] = [
    new SegmentedNavigationItemModel('home', 'Journal'),
    new SegmentedNavigationItemModel('documents', 'Documents'),
    new SegmentedNavigationItemModel('messages', 'Messages'),
  ];

  private dietitian: Dietitian | undefined;
  private dietitianStateSubscription: Subscription;
  private conversationEventSubscription: Subscription;
  private incomingMessageEventSubscription: Subscription;

  constructor(
    myDietitianStateProvider: MyDietitianStateProvider,
    private facade: MyTopbarFacade,
    private incomingMessageEventProvider: IncomingMessageEventProvider,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.dietitianStateSubscription = myDietitianStateProvider.state$.subscribe(
      (state) => {
        if (state && state.loading === LoadingStatus.COMPLETE) {
          this.dietitian = state.entity;
          if (this.dietitian && this.patient.userId) {
            this.facade
              .countNewMessages(
                this.patient.userId.id.toString(),
                this.dietitian.id.toString(),
              )
              .then((count) => {
                this.segmentedItems.filter(
                  (item) => item.id === 'messages',
                )[0].itemsCount = count;
              });
          }
        }
      },
    );

    this.conversationEventSubscription =
      this.facade.conversationEvent$.subscribe((event) => {
        if (event instanceof ConversationRead) {
          if (this.dietitian && this.patient.userId) {
            this.facade
              .countNewMessages(
                this.patient.userId.id.toString(),
                this.dietitian.id.toString(),
              )
              .then((count) => {
                this.segmentedItems.filter(
                  (item) => item.id === 'messages',
                )[0].itemsCount = count;
              });
          }
        }
      });

    this.incomingMessageEventSubscription =
      this.incomingMessageEventProvider.events$.subscribe(() => {
        this.loadNotifications().then();
      });
  }

  ngOnInit(): void {
    this.setActiveItem('home');
    if (this.patient) {
      if (this.patient.isSharedSeries) {
        this.segmentedItems.push(
          new SegmentedNavigationItemModel('charts', 'Courbes'),
        );
      }
      this.loadNotifications().then();
    }
  }

  ngOnDestroy() {
    this.dietitianStateSubscription?.unsubscribe();
    this.conversationEventSubscription?.unsubscribe();
    this.incomingMessageEventSubscription?.unsubscribe();
  }

  onChange(item: SegmentedNavigationItemModel) {
    this.router
      .navigate([item.id], { relativeTo: this.route })
      .then(() => this.setActiveItem(item.id));
  }

  async markAsRead(notification: Notification) {
    if (!this.patient) {
      return;
    }

    this.markingAsReadNotification = notification;

    const notificationToRemove = await this.facade.markNotificationAsRead(
      this.patient.id.toString(),
      notification,
    );

    if (notificationToRemove) {
      // remove the notification from the list
      this.notifications = this.notifications?.filter(
        (obj) =>
          notificationToRemove.notificationId.id.toString() !=
          obj.notificationId.id.toString(),
      );
    }

    this.markingAsReadNotification = undefined;
  }

  async markAsReadAll() {
    if (this.notifications && this.notifications.length > 0) {
      for (const notification of this.notifications) {
        await this.markAsRead(notification);
      }
    }
  }

  openMyAccount() {
    this.router.navigate(['profile'], { relativeTo: this.route }).then(() => {
      this.unsetAllItem();
    });
  }

  async loadNotifications() {
    if (!this.patient) {
      return;
    }

    this.isLoadingNotifications = true;

    return this.facade
      .getPatientNotifications(this.patient?.id?.toString())
      .then((notifications) => {
        this.notifications = notifications;
      })
      .catch((error) => {
        this.notificationsError = error;
      })
      .finally(() => {
        this.isLoadingNotifications = false;
      });
  }

  getTitle(notification: Notification) {
    switch (notification.type) {
      case NotificationType.NEW_DIET_MESSAGE:
        return 'Nouveau message';
      case NotificationType.NEW_ENCOURAGEMENT:
        return 'Nouvel encouragement';
      case NotificationType.DIARY_COMMENT:
        return 'Commentaire de votre diététicien.ne';
      case NotificationType.NEW_SHARED_DOC:
        return 'Nouveau document partagé';
      case NotificationType.NEW_DIET_INVITATION:
        return 'Nouvelle invitation';
      case NotificationType.NEXT_APPOINTMENT_UPDATED:
        return 'Rendez-vous à venir';
      case NotificationType.TEST:
        return 'Nouvelle notification';
    }
    return '';
  }

  getContent(notification: Notification) {
    switch (notification.type) {
      case NotificationType.NEW_DIET_MESSAGE:
        return `Vous avez reçu un nouveau message de votre diététicien.ne
        ${notification.payload.dietitianFirstName} ${notification.payload.dietitianLastName}.`;
      case NotificationType.NEW_ENCOURAGEMENT:
        return `Vous avez reçu un nouvel encouragement de votre diététicien.ne
        ${notification.payload.dietitianFirstName} ${notification.payload.dietitianLastName}.`;
      case NotificationType.DIARY_COMMENT:
        return `Votre diététicien.ne a commenté un événement de votre journal.`;
      case NotificationType.NEW_SHARED_DOC:
        return `Votre diététicien.ne ${notification.payload.dietitianFirstName} ${notification.payload.dietitianLastName}
        vient de partager un nouveau document "${notification.payload.documentName}" avec vous.`;
      case NotificationType.NEW_DIET_INVITATION:
        return `${notification.payload.dietitianFirstName} ${notification.payload.dietitianLastName}
        vous a envoyé une nouvelle invitation.`;
      case NotificationType.NEXT_APPOINTMENT_UPDATED:
        return `Une nouvelle date de rendez-vous a été définie par votre diététicien.ne
        ${notification.payload.dietitianFirstName} ${notification.payload.dietitianLastName}.`;
      case NotificationType.TEST:
        return 'Nouvelle notification';
    }
    return '';
  }

  getAdditionalButton(notification: Notification) {
    if (notification.type === NotificationType.DIARY_COMMENT) {
      return 'Ouvrir le journal';
    }
    return undefined;
  }

  async openNotification(payload: any) {
    const diaryId = payload.diaryId as string;
    const diary = await this.facade.getPatientDiary(
      this.patient.id.toString(),
      diaryId,
    );
    const sunday = moment(diary.createdAt).endOf('week').format('YYYY-MM-DD');
    this.router
      .navigate(['/my', 'home'], {
        queryParams: {
          period: 'week',
          number: 1,
          endDate: sunday,
        },
      })
      .then();
  }

  private setActiveItem(id: string) {
    for (const item of this.segmentedItems) {
      item.isActive = item.id == id;
    }
  }

  private unsetAllItem() {
    for (const item of this.segmentedItems) {
      item.isActive = false;
    }
    this.tabs.unsetAllItems();
  }
}
